import { swiperSlider, destroySlider, debounce } from '@theme/coco-utilities';

if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = null;
      this.slider = null;
      this.params = {};
      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.onResize = debounce(this.handleSlider.bind(this), 150);
      /** @type {(() => void) | null} */
      this._carouselCssVarsUpdate = null;
      /** @type {(() => void) | null} */
      this._carouselTiltApply = null;
      /** @type {(() => void) | null} */
      this._carouselTiltViewportHandler = null;
      /** @type {(() => void) | null} */
      this._carouselTiltSchedule = null;
      /** @type {number} */
      this._carouselTiltRafId = 0;
      /** @type {number} */
      this._carouselTiltTransitionRafId = 0;
      /** @type {(() => void) | null} */
      this._carouselTiltOnTransitionStart = null;

      this._syncTiltOnMq = () => this.syncCarouselTiltViewport();
    }

    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.slider = this.section?.querySelector('[data-slider]') ?? this.querySelector('[data-slider]');
      this.params = this.slider?.dataset.slider
        ? JSON.parse(this.slider.dataset.slider)
        : {};

      if (!this.slider) return;
      this.mediaQuery.addEventListener('change', this.onResize);
      this.mediaQuery.addEventListener('change', this._syncTiltOnMq);
      this.handleSlider();
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.onResize);
      this.mediaQuery.removeEventListener('change', this._syncTiltOnMq);
      this.destroySlider();
    }

    handleSlider() {
      if (!this.sliderInstance) this.init();
    }

    init() {
      // Auto: section 1–2 eager; section 3+ when near viewport (coco-utilities)
      this.sliderInstance = swiperSlider(this.slider, {
        ...this.params,
        onReady: (instance) => {
          this.sliderInstance = instance;
          this.attachCarouselCssVarsIfNeeded();
          this.syncCarouselTiltViewport();
        },
      });
      this.attachCarouselCssVarsIfNeeded();
      this.syncCarouselTiltViewport();
    }

    /**
     * Fan tilt DOM is output when the section has 7+ blocks and tilt is enabled in settings.
     * Runtime: only attach handlers at tablet/desktop (matches Swiper breakpoint at 768px); mobile stays a plain swiper.
     */
    syncCarouselTiltViewport() {
      if (!this.slider?.hasAttribute('data-carousel-tilt')) {
        if (this._carouselTiltApply) this.detachCarouselTilt();
        return;
      }
      if (!this.sliderInstance) return;
      this.attachCarouselTiltIfNeeded();
      // if (this.mediaQuery.matches) {
      //   this.attachCarouselTiltIfNeeded();
      // } else {
      //   this.detachCarouselTilt();
      // }
    }

    destroySlider() {
      this.detachCarouselCssVars();
      this.detachCarouselTilt();

      this.sliderInstance = destroySlider(this.sliderInstance, {
        element: this.slider,
      });
    }

    /**
     * Keeps unitless px values on the swiper root for custom indicators / scroll masks:
     * --carousel-element-width (active slide width), --carousel-element-position (|translate|).
     * Opt in with data-carousel-css-vars on the [data-slider] node.
     */
    attachCarouselCssVarsIfNeeded() {
      if (!this.slider?.hasAttribute('data-carousel-css-vars') || !this.sliderInstance || this._carouselCssVarsUpdate)
        return;

      const swiperEl = this.slider;
      const swiper = this.sliderInstance;

      const update = () => {
        if (!swiper || swiper.destroyed) return;
        const slide = swiper.slides[swiper.activeIndex];
        const width = slide ? slide.getBoundingClientRect().width : 0;
        const pos = Math.abs(swiper.translate);
        swiperEl.style.setProperty('--carousel-element-width', String(width));
        swiperEl.style.setProperty('--carousel-element-position', String(pos));
      };

      swiper.on('slideChange setTranslate transitionEnd resize observerUpdate', update);
      this._carouselCssVarsUpdate = update;
      requestAnimationFrame(() => requestAnimationFrame(update));
    }

    detachCarouselCssVars() {
      if (this._carouselCssVarsUpdate && this.sliderInstance) {
        this.sliderInstance.off(
          'slideChange setTranslate transitionEnd resize observerUpdate',
          this._carouselCssVarsUpdate,
        );
      }
      this._carouselCssVarsUpdate = null;
      if (this.slider?.hasAttribute('data-carousel-css-vars')) {
        this.slider.style.removeProperty('--carousel-element-width');
        this.slider.style.removeProperty('--carousel-element-position');
      }
    }

    /**
     * Fan tilt: slide at viewport/container center is straight (0°). right of center tilts outward right (−deg);
     * right of center tilts outward right (+deg); amount grows with distance, capped at data-carousel-tilt-max.
     * Opt in with data-carousel-tilt on [data-slider]; optional data-carousel-tilt-max (degrees, default 14).
     * Optional data-carousel-tilt-anchor: "viewport" (default) | "container" (swiper root center).
     * Inner targets: elements matching [data-carousel-tilt-inner].
     */
    attachCarouselTiltIfNeeded() {
      if (!this.slider?.hasAttribute('data-carousel-tilt') || !this.sliderInstance || this._carouselTiltApply)
        return;

      const swiper = this.sliderInstance;
      const maxDeg = Number.parseFloat(this.slider.getAttribute('data-carousel-tilt-max') || '14', 10);
      if (!Number.isFinite(maxDeg) || maxDeg <= 0) return;

      const innerAttr = this.slider.getAttribute('data-carousel-tilt-inner');
      const innerSel = innerAttr || '[data-carousel-tilt-inner]';
      const anchor = (this.slider.getAttribute('data-carousel-tilt-anchor') || 'viewport').toLowerCase();

      const flushTilt = () => {
        if (!swiper || swiper.destroyed || !swiper.el) return;

        const swiperRect = swiper.el.getBoundingClientRect();
        let centerX;
        let span;

        if (anchor === 'container') {
          if (!swiperRect.width) return;
          centerX = swiperRect.left + swiperRect.width / 2;
          span = Math.max(swiperRect.width * 0.42, 200);
        } else {
          const vv = window.visualViewport;
          const vw = vv?.width && vv.width > 0 ? vv.width : window.innerWidth;
          const vx = vv?.left ?? 0;
          centerX = vx + vw / 2;
          span = Math.max(vw * 0.38, 200);
        }

        swiper.slides.forEach((slideEl) => {
          const inner = slideEl.querySelector(innerSel);
          if (!inner) return;
          const r = slideEl.getBoundingClientRect();
          const w = r.width || slideEl.offsetWidth || slideEl.clientWidth;
          if (!w) return;
          const slideCenter = r.left + w / 2;
          const dist = slideCenter - centerX;
          let deg = 0;
          let scale = 1;
          if (dist > 0) {
            const norm = Math.min(dist / span, 1);
            deg = norm * maxDeg;
            scale = Math.max(0.91, 1 - norm * 0.085);
          }
          inner.style.transform = `rotate(${deg}deg) scale(${scale})`;
        });
      };

      const scheduleTilt = () => {
        if (this._carouselTiltRafId) return;
        this._carouselTiltRafId = requestAnimationFrame(() => {
          this._carouselTiltRafId = 0;
          flushTilt();
        });
      };

      /** While Swiper runs a CSS transition (arrows, slideTo), setTranslate may not fire every frame — keep tilt in sync with layout. */
      const runTransitionTiltLoop = () => {
        if (!swiper || swiper.destroyed || !swiper.el) {
          this._carouselTiltTransitionRafId = 0;
          return;
        }
        flushTilt();
        const moving = swiper.animating === true;
        if (moving) {
          this._carouselTiltTransitionRafId = requestAnimationFrame(runTransitionTiltLoop);
        } else {
          this._carouselTiltTransitionRafId = 0;
          flushTilt();
        }
      };

      const onTransitionStart = () => {
        if (this._carouselTiltTransitionRafId) {
          cancelAnimationFrame(this._carouselTiltTransitionRafId);
          this._carouselTiltTransitionRafId = 0;
        }
        this._carouselTiltTransitionRafId = requestAnimationFrame(runTransitionTiltLoop);
      };

      this._carouselTiltOnTransitionStart = onTransitionStart;

      swiper.on('setTranslate', scheduleTilt);
      swiper.on('transitionStart', onTransitionStart);
      swiper.on(
        'transitionEnd touchEnd init resize breakpoint slideChange observerUpdate',
        flushTilt,
      );

      this._carouselTiltApply = flushTilt;
      this._carouselTiltSchedule = scheduleTilt;

      if (anchor !== 'container') {
        this._carouselTiltViewportHandler = flushTilt;
        window.visualViewport?.addEventListener('resize', flushTilt);
        window.visualViewport?.addEventListener('scroll', flushTilt);
      }

      requestAnimationFrame(() => requestAnimationFrame(flushTilt));
    }

    detachCarouselTilt() {
      if (this._carouselTiltRafId) {
        cancelAnimationFrame(this._carouselTiltRafId);
        this._carouselTiltRafId = 0;
      }
      if (this._carouselTiltTransitionRafId) {
        cancelAnimationFrame(this._carouselTiltTransitionRafId);
        this._carouselTiltTransitionRafId = 0;
      }

      if (this._carouselTiltViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this._carouselTiltViewportHandler);
        window.visualViewport.removeEventListener('scroll', this._carouselTiltViewportHandler);
      }
      this._carouselTiltViewportHandler = null;

      if (this.sliderInstance) {
        if (this._carouselTiltSchedule) {
          this.sliderInstance.off('setTranslate', this._carouselTiltSchedule);
        }
        if (this._carouselTiltOnTransitionStart) {
          this.sliderInstance.off('transitionStart', this._carouselTiltOnTransitionStart);
        }
        if (this._carouselTiltApply) {
          this.sliderInstance.off(
            'transitionEnd touchEnd init resize breakpoint slideChange observerUpdate',
            this._carouselTiltApply,
          );
        }
      }
      this._carouselTiltSchedule = null;
      this._carouselTiltApply = null;
      this._carouselTiltOnTransitionStart = null;
      if (this.slider?.hasAttribute('data-carousel-tilt')) {
        const innerAttr = this.slider.getAttribute('data-carousel-tilt-inner');
        const innerSel = innerAttr || '[data-carousel-tilt-inner]';
        this.slider.querySelectorAll(innerSel).forEach((el) => {
          el.style.transform = '';
        });
      }
    }
  }

  customElements.define('media-gallery', MediaGallery);
}